/**
 * Products API Module
 * Handles all product-related API calls
 */

import apiClient, { extractData } from '../api-client';

export const productApi = {
  /**
   * List all products with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20, max: 100)
   * @param {string} params.search - Search term
   * @param {boolean} params.is_active - Filter by active status
   * @returns {Promise<Object>} Paginated response with products
   */
  list(params) {
    return apiClient.get('/products', { params }).then(extractData);
  },

  /**
   * Tag vải đang dùng trên sản phẩm "Quần áo thể thao" (gợi ý ô nhập + bộ lọc đơn)
   * @returns {Promise<string[]>}
   */
  fabricTags() {
    return apiClient.get('/products/fabric-tags').then(extractData);
  },

  /**
   * Get product by ID
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Product response
   */
  getById(productId) {
    return apiClient.get(`/products/${productId}`).then(extractData);
  },

  /**
   * Create a new product
   * @param {Object} productData - Product data
   * @param {string} productData.product_code - Product code (required)
   * @param {string} productData.name - Product name (required)
   * @param {number|string} productData.price - Price (required)
   * @param {string} productData.description - Description
   * @returns {Promise<Object>} Created product response
   */
  create(productData) {
    return apiClient.post('/products', productData).then(extractData);
  },

  /**
   * Update product
   * @param {number} productId - Product ID
   * @param {Object} productData - Product data to update
   * @param {string} productData.name - Product name
   * @param {number|string} productData.price - Price
   * @param {string} productData.description - Description
   * @param {boolean} productData.is_active - Active status
   * @returns {Promise<Object>} Updated product response
   */
  update(productId, productData) {
    return apiClient.put(`/products/${productId}`, productData).then(extractData);
  },

  /**
   * Delete product
   * @param {number} productId - Product ID
   * @returns {Promise<Object>} Message response
   */
  delete(productId) {
    return apiClient.delete(`/products/${productId}`).then(extractData);
  },

  /**
   * Upload product main image
   * @param {number} productId - Product ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Updated product response
   */
  uploadImage(productId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/products/${productId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(extractData);
  }
};
